import { useState } from 'react';
import renderMarkdown from '../utils/renderMarkdown';
import s from '../styles/MessageBubble.module.scss';

/**
 * Single chat message bubble.
 * Supports user, assistant, and system (action result / error) roles.
 * Assistant messages render basic markdown (bold, italic, lists, code).
 */
export default function MessageBubble({ message }) {
  const [copied, setCopied] = useState(false);

  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';
  const isError = message.type === 'error';
  const isActionResult = message.type === 'action_result';
  const isActionCancelled = message.type === 'action_cancelled';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (isSystem) {
    return (
      <div className={s.systemWrap}>
        <div
          className={`${s.systemBubble} ${
            isError ? s.error : isActionResult ? (message.success ? s.success : s.error) : s.cancelled
          }`}
        >
          <span className={s.systemIcon}>
            {isError ? '!' : isActionCancelled ? '✕' : message.success ? '✓' : '!'}
          </span>
          <span className={s.systemText}>{message.content}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${s.wrap} ${isUser ? s.wrapUser : s.wrapAssistant}`}>
      {isAssistant && (
        <div className={s.avatar}>
          <span className={s.avatarEmoji}>✦</span>
        </div>
      )}

      <div className={`${s.bubble} ${isUser ? s.bubbleUser : s.bubbleAssistant}`}>
        <div className={s.content}>
          {isAssistant ? renderMarkdown(message.content) : message.content}
        </div>

        {isAssistant && (
          <div className={s.actions}>
            <button className={s.actionBtn} onClick={handleCopy} title="Copy">
              {copied ? (
                <span className={s.copiedIcon}>✓</span>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              )}
            </button>
            <button className={s.actionBtn} title="Regenerate">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Streaming message bubble - shows the currently-streaming assistant response.
 * Also renders markdown in real-time.
 */
export function StreamingBubble({ text }) {
  if (!text) return null;

  return (
    <div className={`${s.wrap} ${s.wrapAssistant}`}>
      <div className={s.avatar}>
        <span className={s.avatarEmoji}>✦</span>
      </div>
      <div className={`${s.bubble} ${s.bubbleAssistant} ${s.streaming}`}>
        <div className={s.content}>{renderMarkdown(text)}</div>
        <span className={s.cursor} />
      </div>
    </div>
  );
}
